import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Button,
  List,
  ListItemIcon,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  InputAdornment
} from '@mui/material';
import LoadingSpinner from '../LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Download,
  Visibility,
  FlightTakeoff,
  Security,
  Warning,
  Assignment,
  Close,
  Search
} from '@mui/icons-material';
import { functions, httpsCallable } from '../../Utils/firebaseConfig';

const OPERATIONS_FOLDER_ID = "1U265bKAGsoZXhHZ1KXoDVv4qnEo7LKOA";

const DocumentActions = ({ title, link }) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Convert Google Drive sharing URL to proper preview/download URLs
  const getGoogleDriveUrls = (shareLink) => {
    // If link is '#' or invalid, return null
    if (!shareLink || shareLink === '#') return null;

    // Extract file ID from various Google Drive URL formats
    const fileId = shareLink.match(/[-\w]{25,}/);
    if (!fileId) return null;

    return {
      previewUrl: `https://drive.google.com/file/d/${fileId[0]}/preview`,
      downloadUrl: `https://drive.google.com/uc?export=download&id=${fileId[0]}`
    };
  };

  const urls = getGoogleDriveUrls(link);
  const isFileAvailable = Boolean(urls);

  const handleDownload = (e) => {
    if (!isFileAvailable) {
      e.preventDefault();
    }
  };

  const handlePreview = () => {
    if (isFileAvailable) {
      setOpenPreview(true);
      setError(false);
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setError(true);
    setIsLoading(false);
  };

  return (
    <>
      <Box sx={{
        display: 'flex',
        gap: 1,
        height: '100%',
        ml: { xs: 0, sm: 2 },
        mt: { xs: 1, sm: 0 },
        flexDirection: { xs: 'row', sm: 'row' },
        width: { xs: '100%', sm: 'auto' },
        whiteSpace: 'nowrap'
      }}>
        <Button
          fullWidth
          startIcon={<Download sx={{ color: isFileAvailable ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.38)' }} />}
          href={urls?.downloadUrl || '#'}
          onClick={handleDownload}
          target="_blank"
          rel="noopener noreferrer"
          variant="text"
          disabled={!isFileAvailable}
          sx={{
            color: isFileAvailable ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.38)',
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            fontWeight: 400,
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: isFileAvailable ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
            },
            cursor: isFileAvailable ? 'pointer' : 'not-allowed'
          }}
          title={isFileAvailable ? 'Last ned dokument' : 'Dokumentet er ikke tilgjengelig'}
        >
          Last ned
        </Button>
        <Button
          fullWidth
          startIcon={<Visibility sx={{ color: isFileAvailable ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.38)' }} />}
          onClick={handlePreview}
          variant="text"
          disabled={!isFileAvailable}
          sx={{
            color: isFileAvailable ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.38)',
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            fontWeight: 400,
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: isFileAvailable ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
            },
            cursor: isFileAvailable ? 'pointer' : 'not-allowed'
          }}
          title={isFileAvailable ? 'Vis dokument' : 'Dokumentet er ikke tilgjengelig'}
        >
          Vis
        </Button>
      </Box>

      <Dialog
        fullScreen
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setIsLoading(true);
          setError(false);
        }}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        <DialogTitle sx={{
          m: 0,
          p: 2,
          backgroundColor: '#fff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <IconButton
            onClick={() => {
              setOpenPreview(false);
              setIsLoading(true);
              setError(false);
            }}
            sx={{ color: '#2e2e2e' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                zIndex: 1
              }}
            >
              <LoadingSpinner size={40} fullHeight={false} />
            </Box>
          )}
          {error ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                backgroundColor: '#f5f5f5',
                color: 'rgba(0, 0, 0, 0.6)',
                flexDirection: 'column',
                gap: 2,
                p: 3,
                textAlign: 'center'
              }}
            >
              <Typography variant="h6">
                Kunne ikke laste inn dokumentet
              </Typography>
              <Typography variant="body1">
                Dokumentet er ikke tilgjengelig for øyeblikket. Vennligst prøv igjen senere eller kontakt systemadministrator.
              </Typography>
            </Box>
          ) : (
            <iframe
              src={urls?.previewUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={title}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const ResourceSection = ({ title, description, icon, documents }) => (
  <Card sx={{
    backgroundColor: '#ffffff',
    borderRadius: 0,
    boxShadow: 'none',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    mb: 3
  }}>
    <Box sx={{
      p: 1.5,
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      display: 'flex',
      alignItems: 'center'
    }}>
      <ListItemIcon sx={{ color: '#3598b7', minWidth: 40 }}>
        {icon}
      </ListItemIcon>
      <Box>
        <Typography variant="h6" sx={{ color: '#3598b7', fontWeight: 600, fontSize: '1.1rem' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(46, 46, 46, 0.7)', fontSize: '0.85rem' }}>
          {description}
        </Typography>
      </Box>
    </Box>
    <List disablePadding dense>
      {documents.map((doc, index) => (
        <React.Fragment key={index}>
          <Box sx={{
            py: 0.75,
            px: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            minHeight: { sm: '40px' }
          }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: doc.isError ? 'error.main' : '#2e2e2e',
                mb: { xs: 0.5, sm: 0 },
                fontSize: { xs: '0.95rem', sm: '0.9rem' },
                fontWeight: { xs: 500, sm: 400 },
                flex: { sm: 1 },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {doc.isLoading && <LoadingSpinner size={16} fullHeight={false} />}
              {doc.title}
            </Typography>
            {!doc.isLoading && !doc.isError && <DocumentActions title={doc.title} link={doc.link} />}
          </Box>
          {index < documents.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  </Card>
);

const Operations = () => {
  const navigate = useNavigate();
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchManuals = async () => {
      try {
        console.log('[Operations] Creating function callable');
        const listFolderContents = httpsCallable(functions, 'listFolderContents');
        console.log('[Operations] Function callable created');
        
        console.log('[Operations] Calling function with folderId:', OPERATIONS_FOLDER_ID);
        
        // Add timeout to prevent hanging requests
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 30000)
        );
        
        console.log('[Operations] Starting function call');
        const response = await Promise.race([
          listFolderContents({ folderId: OPERATIONS_FOLDER_ID }),
          timeoutPromise
        ]);
        
        console.log('[Operations] Function response received:', response);
        console.log('[Operations] Response data:', response.data);
        
        if (response.data.success) {
          const mappedFiles = response.data.files.map(file => ({
            title: file.name,
            link: file.viewLink
          }));
          console.log('[Operations] Mapped files:', mappedFiles);
          setManuals(mappedFiles);
        } else {
          console.error('[Operations] Response indicated failure:', response.data);
          setError('Failed to fetch manuals');
        }
      } catch (err) {
        console.error('[Operations] Error in fetchManuals:', err);
        console.error('[Operations] Error details:', {
          message: err.message,
          code: err.code,
          stack: err.stack
        });
        setError(err.message || 'An error occurred while fetching manuals');
      } finally {
        setLoading(false);
      }
    };

    console.log('[Operations] Starting fetchManuals');
    fetchManuals();
  }, []);

  // Add logging for manuals state changes
  useEffect(() => {
    console.log('[Operations] Manuals state updated:', manuals);
  }, [manuals]);

  const sections = [
    {
      title: 'Standard Operating Procedures',
      description: 'Standardiserte prosedyrer for sikker flyging',
      icon: <FlightTakeoff />,
      documents: [
        { title: 'SOP - Generell Flyging', link: '#' },
        { title: 'SOP - Spesialoperasjoner', link: '#' },
        { title: 'SOP - Vedlikehold', link: '#' }
      ]
    },
    {
      title: 'Nødprosedyrer',
      description: 'Prosedyrer for nødsituasjoner og uventede hendelser',
      icon: <Warning />,
      documents: [
        { title: 'Emergency Response Plan', link: '#' },
        { title: 'Nødlandingsprosedyrer', link: '#' }
      ]
    },
    {
      title: 'Sikkerhetsprotokoller',
      description: 'Generelle sikkerhetsrutiner og retningslinjer',
      icon: <Security />,
      documents: [
        { title: 'SORA - DJI Inspire 3', link: 'https://drive.google.com/file/d/13NscKcOgvjPGk9PGitZ9h-emXZFkm834/view?usp=drive_link' },
        { title: 'SORA - Freefly Alta X', link: 'https://drive.google.com/file/d/1kPdKtnDDRBSJiyJst4124dTmhUV32bsI/view?usp=drive_link' },
        { title: 'Sikkerhetsmanual', link: '#' },
        { title: 'Risikovurdering', link: '#' }
      ]
    },
    {
      title: 'Operasjonsmanualer',
      description: 'Tekniske manualer og operasjonelle dokumenter',
      icon: <Assignment />,
      documents: loading ? [{ title: 'Laster inn dokumenter...', link: '#', isLoading: true }] : error ? [{ title: 'Kunne ikke laste inn dokumenter', link: '#', isError: true }] : manuals
    }
  ];

  console.log('[Operations] Current sections:', sections);
  console.log('[Operations] Loading state:', loading);
  console.log('[Operations] Error state:', error);

  const filteredSections = sections.map(section => ({
    ...section,
    documents: section.documents.filter(doc => 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.documents.length > 0);

  console.log('[Operations] Filtered sections:', filteredSections);

  return (
    <Box sx={{
      p: 3,
      maxWidth: 1200,
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#2e2e2e', fontWeight: 600, mb: 1 }}>
          Pilot Ressurser
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => navigate('/pilot')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ color: '#3598b7', fontWeight: 600 }}>
            Flyoperasjoner
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Søk i dokumenter..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            backgroundColor: '#fff',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.12)',
              },
              '&:hover fieldset': {
                borderColor: '#3598b7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3598b7',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {filteredSections.map((section, index) => (
        <ResourceSection key={index} {...section} />
      ))}

      {filteredSections.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4, color: 'rgba(0, 0, 0, 0.6)' }}>
          <Typography>
            Ingen dokumenter funnet for "{searchQuery}"
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Operations; 